import React, { useCallback, useEffect, useRef, useState } from 'react';

import styled, { css, keyframes } from 'styled-components';

export type FlashCardData = {
  title: string;
  color: string;
  frontDescription: string;
  frontImage: React.ReactNode;
  backContent: React.ReactNode;
};

interface FlashCardProps {
  card: FlashCardData;
  /** If true, plays a partial-flip hint animation when scrolled into view */
  hintFlip?: boolean;
}

const peekFlip = keyframes`
  0% { transform: rotateY(0deg); }
  40% { transform: rotateY(54deg); }
  100% { transform: rotateY(0deg); }
`;

const CardContainer = styled.div`
  perspective: 1000px;
  width: 368px;
  height: 470px;
  cursor: pointer;
  flex-shrink: 0;

  @media (max-width: 768px) {
    width: 340px;
    height: 470px;
  }
`;

const CardInner = styled.div<{ $hinting: boolean; $flipped: boolean }>`
  position: relative;
  width: 100%;
  height: 100%;
  transition: transform 0.6s ease;
  transform-style: preserve-3d;

  @media (hover: hover) {
    ${CardContainer}:hover & {
      transform: rotateY(180deg);
    }
  }

  ${({ $flipped }) =>
    $flipped &&
    css`
      transform: rotateY(180deg);
    `}

  ${({ $hinting }) =>
    $hinting &&
    css`
      animation: ${peekFlip} 1s ease-in-out;
    `}
`;

const CardFace = styled.div`
  position: absolute;
  width: 100%;
  height: 100%;
  backface-visibility: hidden;
  border-radius: 32px;
  background: var(--white);
  box-shadow: 0px 5px 11px rgba(0, 0, 0, 0.07), 0px 19px 19px rgba(0, 0, 0, 0.06),
    0px 43px 26px rgba(0, 0, 0, 0.04);
  display: flex;
  flex-direction: column;
  align-items: center;
  overflow: hidden;
`;

const Front = styled(CardFace)`
  padding: 35px 19px;
  text-align: center;
  gap: 12px;
`;

const FrontTitle = styled.h5<{ $color: string }>`
  font-family: Boing;
  font-size: 40px;
  font-weight: 500;
  line-height: 45px;
  color: ${({ $color }) => $color};
  margin: 0;
  width: 330px;

  @media (max-width: 768px) {
    font-size: 26px;
    line-height: 28px;
  }
`;

const FrontDescription = styled.p`
  font-size: 16px;
  line-height: 24px;
  color: #393939;
  margin: 0;
  max-width: 313px;
  text-align: center;
`;

const FrontImageWrapper = styled.div`
  width: 330px;
  height: 280px;
  overflow: hidden;
  flex-shrink: 0;
  margin-top: auto;

  img,
  .gatsby-image-wrapper {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }

  @media (max-width: 768px) {
    width: 302px;
    height: 256px;
  }
`;

const Back = styled(CardFace)<{ $color: string }>`
  transform: rotateY(180deg);
  outline: 10px ${({ $color }) => $color} solid;
  outline-offset: -10px;
  padding: 35px 19px;
`;

const BackContent = styled.div`
  max-width: 313px;
  display: flex;
  flex-direction: column;
  gap: 16px;
  font-size: 14px;
  line-height: 21px;
  color: var(--stile-grey-8);

  .section-label {
    color: var(--stile-grey-6);
    font-weight: 600;
    font-size: 14px;
    line-height: 21px;
    margin: 0;
  }

  .section-body {
    font-size: 14px;
    line-height: 18px;
    margin: 0;
  }

  .section {
    display: flex;
    flex-direction: column;
    gap: 2px;
  }
`;

const FlashCard: React.FC<FlashCardProps> = ({ card, hintFlip = false }) => {
  const [isFlipped, setIsFlipped] = useState(false);
  const [hasHinted, setHasHinted] = useState(false);
  const [shouldHint, setShouldHint] = useState(false);
  const cardRef = useRef<HTMLDivElement>(null);

  const onAnimationEnd = useCallback(() => setHasHinted(true), []);

  useEffect(() => {
    if (!hintFlip || shouldHint) return;

    const el = cardRef.current;
    if (!el) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setTimeout(() => setShouldHint(true), 400);
          observer.disconnect();
        }
      },
      { threshold: 0.6 }
    );

    observer.observe(el);
    return () => observer.disconnect();
  }, [hintFlip, shouldHint]);

  const isHinting = hintFlip && shouldHint && !hasHinted;

  return (
    <CardContainer ref={cardRef} onClick={() => setIsFlipped((f) => !f)}>
      <CardInner $hinting={isHinting} $flipped={isFlipped} onAnimationEnd={onAnimationEnd}>
        <Front>
          <FrontTitle $color={card.color}>{card.title}</FrontTitle>
          <FrontDescription>{card.frontDescription}</FrontDescription>
          <FrontImageWrapper>{card.frontImage}</FrontImageWrapper>
        </Front>
        <Back $color={card.color}>
          <BackContent>{card.backContent}</BackContent>
        </Back>
      </CardInner>
    </CardContainer>
  );
};

export default FlashCard;
